// ===================================
// KAIXO CRM - Modal Principal
// Módulo aislado, Portal en document.body
// ===================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './CRM.css';
import { Icons } from '../Icons/Icons';
import ct from './i18n';
import CRMDashboard from './CRMDashboard';
import CRMReports from './CRMReports';
import CRMActivity from './CRMActivity';
import CRMLeads from './CRMLeads';
import CRMPipelineWeb from './CRMPipelineWeb';
import CRMAgenda from './CRMAgenda';
import LeadNotes from './LeadNotes';
import CRMAgents from './CRMAgents';
import CRMAds from './CRMAds';
import {
    crmActivate, crmGetDashboard, crmGetLeads, crmCreateLead, crmUpdateLead,
    crmDeleteLead, crmGetActivity, crmGetReports,
    crmGetAgents, crmGetAgentDetail, crmUpdateAgent, crmAddAgent, crmRemoveAgent,
    crmGetBestAds, crmAssignUnassigned
} from '../../services/crmApi';
import { getAllUsers } from '../../services/api';
import { boostListMine, boostPause, boostResume, boostCancel, boostStats } from '../../services/boostsApi';
import { getSocket } from '../../services/socket';
import PlansModal from '../Plans/PlansModal';
import ImpulsaGuide from '../Impulsa/ImpulsaGuide';
import useModalClose from '../../hooks/useModalClose';

const STATUS_COLORS = {
    nuevo: '#10B981', contactado: '#3B82F6', interesado: '#F59E0B',
    propuesta: '#8B5CF6', negociacion: '#EC4899', cerrado: '#059669', perdido: '#EF4444'
};

function CRMModal({ currentUser, onClose, embedded = false }) {
    useModalClose(onClose, !embedded);
    const [view, setView] = useState('dashboard');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // En pantalla ancha (kaixo web), la pestaña "Contactos" muestra el
    // kanban pipeline en lugar de la lista clásica. En móvil queda intacto.
    const [isWideScreen, setIsWideScreen] = useState(() => {
        try { return window.matchMedia('(min-width: 769px)').matches; } catch { return false; }
    });
    useEffect(() => {
        try {
            const mq = window.matchMedia('(min-width: 769px)');
            const handler = (e) => setIsWideScreen(e.matches);
            mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
            return () => mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
        } catch {}
    }, []);

    // Dashboard
    const [dashboard, setDashboard] = useState(null);
    // Leads
    const [leads, setLeads] = useState([]);
    const [totalLeads, setTotalLeads] = useState(0);
    const [filters, setFilters] = useState({ status: '', search: '', page: 1 });
    // Lead form
    const [editingLead, setEditingLead] = useState(null); // null=nuevo, object=editar
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({});
    // Activity
    const [activities, setActivities] = useState([]);
    // Reports
    const [reports, setReports] = useState(null);
    // Agents
    const [agents, setAgents] = useState([]);
    const [boosts, setBoosts] = useState([]);
    const [boostStatsMap, setBoostStatsMap] = useState({}); // { boostId: [impr per day] }
    const [agentDetail, setAgentDetail] = useState(null); // { agent, stats, leads }
    const [activityFilters, setActivityFilters] = useState({ agentId: '', status: '', language: '', country: '', region: '' });
    const [bestAds, setBestAds] = useState([]);
    const [allKaixoUsers, setAllKaixoUsers] = useState([]);
    const [showAddAgent, setShowAddAgent] = useState(false);
    const [newAgentId, setNewAgentId] = useState('');
    const [newAgentRole, setNewAgentRole] = useState('agent');

    const loadDashboard = async () => {
        setLoading(true);
        try {
            const { data } = await crmGetDashboard();
            setDashboard(data.dashboard);
            setAgents(data.agents || []);
        } catch (e) { setError(e.response?.data?.error || e.message); }
        setLoading(false);
    };

    const loadLeads = async (f = filters) => {
        setLoading(true);
        try {
            const { data } = await crmGetLeads({ ...f, limit: 50 });
            setLeads(data.leads || []);
            setTotalLeads(data.total || 0);
        } catch (e) { setError(e.response?.data?.error || e.message); }
        setLoading(false);
    };

    const loadActivity = async (filters = activityFilters) => {
        try {
            const cleanFilters = Object.fromEntries(
                Object.entries(filters).filter(([, v]) => v)
            );
            const { data } = await crmGetActivity({ limit: 30, ...cleanFilters });
            setActivities(data.activities || []);
        } catch {}
    };

    const loadBestAds = async () => {
        try {
            const { data } = await crmGetBestAds();
            setBestAds(data.ads || []);
        } catch {}
    };

    const loadReports = async () => {
        try {
            const { data } = await crmGetReports({});
            setReports(data.reports);
        } catch {}
    };

    const loadAgents = async () => {
        try {
            const [agRes, usersRes] = await Promise.all([crmGetAgents(), getAllUsers()]);
            setAgents(agRes.data.agents || []);
            setAllKaixoUsers(usersRes.data.users || []);
        } catch {}
    };

    const loadBoosts = async () => {
        try {
            const { data } = await boostListMine();
            const list = data.boosts || [];
            setBoosts(list);
            // Cargar stats en paralelo (no bloqueante)
            const statsPromises = list.map(b =>
                boostStats(b._id)
                    .then(({ data: s }) => ({ id: b._id, series: (s.daily || []).map(d => d.impressions) }))
                    .catch(() => ({ id: b._id, series: [] }))
            );
            Promise.all(statsPromises).then(results => {
                const map = {};
                results.forEach(r => { map[r.id] = r.series; });
                setBoostStatsMap(map);
            });
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const handleBoostAction = async (boostId, action) => {
        try {
            if (action === 'pause') await boostPause(boostId);
            else if (action === 'resume') await boostResume(boostId);
            else if (action === 'cancel') {
                if (!window.confirm('¿Cancelar este anuncio? Se devolverá el saldo no gastado.')) return;
                await boostCancel(boostId);
            }
            loadBoosts();
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const handleAddAgent = async () => {
        if (!newAgentId) return;
        try {
            await crmAddAgent({ agentUserId: newAgentId, role: newAgentRole });
            setShowAddAgent(false);
            setNewAgentId('');
            setNewAgentRole('agent');
            loadAgents();
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const handleRemoveAgent = async (agentId) => {
        if (!window.confirm(ct.removeAgent + '?')) return;
        try {
            await crmRemoveAgent(agentId);
            setAgentDetail(null);
            loadAgents();
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const openAgentDetail = async (agentId) => {
        try {
            const { data } = await crmGetAgentDetail(agentId);
            setAgentDetail(data);
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const handleAssignUnassigned = async () => {
        try {
            const { data } = await crmAssignUnassigned();
            if (data.assigned > 0) {
                setError('');
                alert(`✓ ${data.assigned} lead${data.assigned === 1 ? '' : 's'} asignado${data.assigned === 1 ? '' : 's'} automáticamente`);
                loadLeads();
                loadDashboard();
            } else {
                alert(data.message || 'No hay leads sin asignar');
            }
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const handleUpdateAgentRole = async (agentId, role) => {
        try {
            await crmUpdateAgent(agentId, { role });
            if (agentDetail?.agent?._id === agentId) {
                setAgentDetail(d => ({ ...d, agent: { ...d.agent, role } }));
            }
            loadAgents();
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    useEffect(() => {
        crmActivate().catch(() => {}).finally(() => loadDashboard());
    }, []); // eslint-disable-line

    useEffect(() => {
        if (view === 'leads') loadLeads();
        if (view === 'activity') loadActivity();
        if (view === 'reports') loadReports();
        if (view === 'agents') loadAgents();
        if (view === 'ads') { loadBoosts(); loadBestAds(); }
    }, [view]); // eslint-disable-line

    // Realtime: el backend emite eventos cuando un lead cambia (creado,
    // editado, status movido, borrado). Cubre el caso "tengo web + móvil
    // abiertos al mismo tiempo": al mover un lead en uno, el otro refresca
    // sin recargar manualmente. Listener registrado en cada vista activa
    // para que solo se recargue lo que está siendo visto.
    useEffect(() => {
        const s = typeof getSocket === 'function' ? getSocket() : null;
        if (!s) return;
        const handler = () => {
            if (view === 'leads') loadLeads();
            if (view === 'activity') loadActivity();
            if (view === 'dashboard') loadDashboard();
        };
        // Evento legado (formulario de contacto desde anuncio) + nuevos
        // eventos namespaced del CRM que añadimos en updateLead/createLead/deleteLead.
        s.on('crm_lead_created', handler);
        s.on('crm:lead_created', handler);
        s.on('crm:lead_updated', handler);
        s.on('crm:lead_deleted', handler);
        return () => {
            try {
                s.off('crm_lead_created', handler);
                s.off('crm:lead_created', handler);
                s.off('crm:lead_updated', handler);
                s.off('crm:lead_deleted', handler);
            } catch {}
        };
    }, [view]); // eslint-disable-line

    // Listener del sidebar de actividad (CRMActivitySidebar): cuando el
    // usuario pulsa una actividad, dispara window event 'crm:open_lead'
    // con { leadId }. Aquí cargamos el lead y abrimos el editor.
    useEffect(() => {
        const handler = async (e) => {
            const leadId = e?.detail?.leadId;
            if (!leadId) return;
            try {
                // Buscar primero en los leads ya cargados — evita un fetch.
                const cached = leads.find(l => String(l._id) === String(leadId));
                if (cached) {
                    setView('leads');
                    openEditLead(cached);
                    return;
                }
                // Si no está cacheado, recargar la lista entera y abrir.
                // crmGetLeads ya está disponible vía api wrapper.
                const { crmGetLeads } = await import('../../services/crmApi');
                const r = await crmGetLeads({ limit: 200 });
                const list = r?.data?.leads || [];
                const found = list.find(l => String(l._id) === String(leadId));
                setLeads(list);
                if (found) {
                    setView('leads');
                    openEditLead(found);
                }
            } catch (err) {
                console.warn('open_lead from activity:', err?.message);
            }
        };
        window.addEventListener('crm:open_lead', handler);
        return () => window.removeEventListener('crm:open_lead', handler);
    }, [leads]); // eslint-disable-line

    const openNewLead = () => {
        setEditingLead(null);
        setForm({
            firstName: '', lastName: '', phone: '', email: '',
            source: 'manual', status: 'nuevo',
            budget: '', product: '', notes: '',
            actionDate: '', actionTime: '', actionType: 'call', actionNote: ''
        });
        setShowForm(true);
    };

    // Descargar/compartir la base de leads como CSV.
    // - Navegador (Kaixo Web): descarga clásica con <a download>.
    // - Capacitor (APK Android): el WebView NO dispara <a download>. Guarda el
    //   archivo en el cache del dispositivo y abre el share sheet del sistema
    //   para que el user lo pase a Drive/email/WhatsApp/Archivos/donde quiera.
    // Backend requiere plan canExportCsv → si no, dispara UpgradeModal.
    const handleExportCsv = async () => {
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.source) params.append('source', filters.source);

            const token = localStorage.getItem('kaixo_token') || '';
            const apiBase = process.env.REACT_APP_API_URL || 'https://backend-dlmm.onrender.com/api';
            const resp = await fetch(`${apiBase}/crm/leads/export?${params.toString()}`, {
                method: 'GET',
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (!resp.ok) {
                const data = await resp.json().catch(() => ({}));
                if (resp.status === 403 && data.error === 'plan_limit_exceeded') {
                    window.dispatchEvent(new CustomEvent('kaixo-upgrade-required', { detail: data }));
                    return;
                }
                alert('No se pudo exportar el CSV: ' + (data.message || data.error || resp.status));
                return;
            }

            const csvText = await resp.text();
            const fileName = `kaixo-leads-${new Date().toISOString().slice(0, 10)}.csv`;

            // Detectar Capacitor (APK) — share sheet nativo + filesystem
            const isCapacitor = typeof window !== 'undefined' &&
                (window.location.protocol === 'capacitor:' ||
                 (window.location.protocol === 'https:' && window.location.hostname === 'localhost'));

            if (isCapacitor) {
                try {
                    // eslint-disable-next-line global-require, import/no-unresolved
                    const { Filesystem, Directory, Encoding } = require('@capacitor/filesystem');
                    // eslint-disable-next-line global-require, import/no-unresolved
                    const { Share } = require('@capacitor/share');

                    const writeResult = await Filesystem.writeFile({
                        path: fileName,
                        data: csvText,
                        directory: Directory.Cache,
                        encoding: Encoding.UTF8
                    });
                    await Share.share({
                        title: 'Exportación CSV de contactos KAIXO',
                        text: 'Tus contactos KAIXO',
                        url: writeResult.uri,
                        dialogTitle: 'Guardar o compartir CSV'
                    });
                    return;
                } catch (e) {
                    console.warn('[CRM export] share nativo falló, fallback a copiar:', e?.message);
                    try { await navigator.clipboard.writeText(csvText); } catch {}
                    alert('El archivo CSV se copió al portapapeles. Pégalo en Notas o Drive y guárdalo con extensión .csv.');
                    return;
                }
            }

            // Navegador normal: descarga clásica
            const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            console.error('[CRM export CSV]', err);
            alert('Error al exportar: ' + (err?.message || 'desconocido'));
        }
    };

    const openEditLead = (lead) => {
        setEditingLead(lead);
        // Pasamos nextAction al form como atDate (yyyy-mm-dd) + atTime (HH:MM)
        // separados, más cómodo para el usuario que un datetime único.
        let actionDate = '', actionTime = '';
        if (lead.nextAction?.at) {
            try {
                const d = new Date(lead.nextAction.at);
                if (!isNaN(d.getTime())) {
                    const pad = (n) => String(n).padStart(2, '0');
                    actionDate = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
                    actionTime = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
                }
            } catch {}
        }
        setForm({
            firstName: lead.firstName || '', lastName: lead.lastName || '',
            phone: lead.phone || '', email: lead.email || '',
            source: lead.source || 'manual', status: lead.status || 'nuevo',
            budget: lead.budget || '', product: lead.product || '',
            notes: lead.notes || '', assignedTo: lead.assignedTo?._id || '',
            score: lead.score || 0, lostReason: lead.lostReason || '',
            // Próxima acción (split en date + time + type + note)
            actionDate, actionTime,
            actionType: lead.nextAction?.type || 'call',
            actionNote: lead.nextAction?.note || ''
        });
        setShowForm(true);
    };

    const handleSaveLead = async () => {
        if (!form.firstName?.trim()) { setError(ct.firstName + ' required'); return; }
        // Separamos los campos del lead "estándar" de los de nextAction
        // (no son columnas directas del modelo Lead — tienen su propio
        // sub-doc con su endpoint dedicado).
        const { actionDate, actionTime, actionType, actionNote, ...leadFields } = form;
        try {
            let savedLead;
            if (editingLead) {
                const r = await crmUpdateLead(editingLead._id, leadFields);
                savedLead = r?.data?.lead || editingLead;
            } else {
                const r = await crmCreateLead(leadFields);
                savedLead = r?.data?.lead;
            }

            // Persistir nextAction si el usuario rellenó fecha+hora.
            // Si los borró (campos vacíos), limpiamos la acción.
            if (savedLead?._id) {
                const { crmSetNextAction } = await import('../../services/crmApi');
                if (actionDate && actionTime) {
                    const at = new Date(`${actionDate}T${actionTime}:00`);
                    if (!isNaN(at.getTime())) {
                        await crmSetNextAction(savedLead._id, {
                            at: at.toISOString(),
                            type: actionType || 'call',
                            note: actionNote || ''
                        });
                    }
                } else if (editingLead?.nextAction?.at && !actionDate && !actionTime) {
                    // Usuario borró fecha y hora deliberadamente → limpiar
                    await crmSetNextAction(savedLead._id, { at: null });
                }
            }

            setShowForm(false);
            setEditingLead(null);
            loadLeads();
            loadDashboard();
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const handleDeleteLead = async (leadId) => {
        if (!window.confirm(ct.confirmDelete)) return;
        try {
            await crmDeleteLead(leadId);
            loadLeads();
            loadDashboard();
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const handleStatusChange = async (leadId, newStatus) => {
        try {
            await crmUpdateLead(leadId, { status: newStatus });
            loadLeads();
            loadDashboard();
        } catch {}
    };

    // Ref + estado para detectar si hay más pestañas a la izquierda/derecha
    const navScrollRef = useRef(null);
    const [navScrollState, setNavScrollState] = useState({ atStart: true, atEnd: false });

    const updateNavScrollState = useCallback(() => {
        const el = navScrollRef.current;
        if (!el) return;
        const atStart = el.scrollLeft <= 4;
        const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
        setNavScrollState({ atStart, atEnd });
    }, []);

    useEffect(() => {
        updateNavScrollState();
        const el = navScrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', updateNavScrollState, { passive: true });
        window.addEventListener('resize', updateNavScrollState);
        return () => {
            el.removeEventListener('scroll', updateNavScrollState);
            window.removeEventListener('resize', updateNavScrollState);
        };
    }, [updateNavScrollState]);

    // Al cambiar de pestaña, centra la activa en la vista (especialmente útil en móvil)
    useEffect(() => {
        const el = navScrollRef.current;
        if (!el) return;
        const activeBtn = el.querySelector('.crm-nav-btn.active');
        if (activeBtn) {
            const left = activeBtn.offsetLeft - el.clientWidth / 2 + activeBtn.clientWidth / 2;
            el.scrollTo({ left, behavior: 'smooth' });
        }
    }, [view]);

    const navWrapClass = [
        'crm-nav-wrap',
        navScrollState.atStart ? 'at-start' : 'scrolled',
        navScrollState.atEnd ? 'at-end' : ''
    ].join(' ').trim();

    const crmNav = (
        <div className={navWrapClass}>
            <div className="crm-nav" ref={navScrollRef}>
                {['dashboard', 'agenda', 'leads', 'reports', 'agents', 'activity', 'ads', 'plans', 'guide'].map(tab => (
                    <button key={tab} className={`crm-nav-btn ${view === tab ? 'active' : ''}`}
                        onClick={() => setView(tab)}>
                        {tab === 'dashboard' && <Icons.Home size={14} />}
                        {tab === 'agenda' && <Icons.Calendar size={14} />}
                        {tab === 'leads' && <Icons.Users size={14} />}
                        {tab === 'reports' && <Icons.Search size={14} />}
                        {tab === 'agents' && <Icons.Group size={14} />}
                        {tab === 'activity' && <Icons.List size={14} />}
                        {tab === 'ads' && <Icons.Pulse size={14} />}
                        {tab === 'plans' && <Icons.Star size={14} />}
                        {tab === 'guide' && <Icons.Info size={14} />}
                        <span className="crm-nav-label">{ct[tab] || (tab === 'agenda' ? 'Agenda' : tab)}</span>
                    </button>
                ))}
            </div>
        </div>
    );

    const crmBody = (
        <div className="crm-body">
                    {/* ── DASHBOARD ── */}
                    {view === 'dashboard' && (
                        <CRMDashboard
                            dashboard={dashboard}
                            loading={loading}
                            statusColors={STATUS_COLORS}
                            onNewLead={() => { setView('leads'); openNewLead(); }}
                        />
                    )}

                    {/* ── AGENDA ── pendientes / hoy / atrasadas */}
                    {view === 'agenda' && (
                        <CRMAgenda
                            onOpenLead={(lead) => { setView('leads'); openEditLead(lead); }}
                        />
                    )}

                    {/* ── LEADS / CONTACTOS ──
                        Web: kanban con drag & drop (CRMPipelineWeb).
                        Móvil: lista clásica con selector de status (CRMLeads). */}
                    {view === 'leads' && isWideScreen && (
                        <CRMPipelineWeb
                            currentUser={currentUser}
                            leads={leads}
                            loading={loading}
                            onReload={() => loadLeads()}
                            onEditLead={openEditLead}
                            onNewLead={openNewLead}
                            onStatusChange={handleStatusChange}
                        />
                    )}
                    {view === 'leads' && !isWideScreen && (
                        <CRMLeads
                            leads={leads}
                            totalLeads={totalLeads}
                            loading={loading}
                            filters={filters}
                            onFiltersChange={(f) => { setFilters(f); loadLeads(f); }}
                            statusColors={STATUS_COLORS}
                            onExportCsv={handleExportCsv}
                            onNewLead={openNewLead}
                            onEditLead={openEditLead}
                            onStatusChange={handleStatusChange}
                        />
                    )}

                    {/* ── REPORTS ── */}
                    {view === 'reports' && (
                        <CRMReports reports={reports} />
                    )}

                    {/* ── AGENTS ── */}
                    {view === 'agents' && (
                        <CRMAgents
                            agents={agents}
                            currentUser={currentUser}
                            allKaixoUsers={allKaixoUsers}
                            showAddAgent={showAddAgent}
                            onToggleAddAgent={() => setShowAddAgent(!showAddAgent)}
                            newAgentId={newAgentId}
                            onNewAgentIdChange={setNewAgentId}
                            newAgentRole={newAgentRole}
                            onNewAgentRoleChange={setNewAgentRole}
                            onAddAgent={handleAddAgent}
                            onAssignUnassigned={handleAssignUnassigned}
                            onOpenDetail={openAgentDetail}
                        />
                    )}

                    {/* ── ACTIVITY ── */}
                    {view === 'activity' && (
                        <CRMActivity
                            activities={activities}
                            agents={agents}
                            filters={activityFilters}
                            onFilterChange={setActivityFilters}
                            onLoad={loadActivity}
                        />
                    )}

                    {/* ── ANUNCIOS ── */}
                    {view === 'ads' && (
                        <CRMAds
                            boosts={boosts}
                            boostStatsMap={boostStatsMap}
                            bestAds={bestAds}
                            onBoostAction={handleBoostAction}
                        />
                    )}

                    {/* ── PLANES ── Usa el mismo PlansModal global (modo beta, 5 tarjetas) */}
                    {view === 'plans' && (
                        <PlansModal onClose={() => setView('dashboard')} />
                    )}

                    {/* ── GUÍA ── */}
                    {view === 'guide' && <ImpulsaGuide />}
                </div>
    );

    const agentDetailModal = agentDetail && (
        <div className="crm-form-overlay" onClick={() => setAgentDetail(null)}>
            <div className="crm-form-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                    <div className="crm-agent-avatar" style={{ width: 64, height: 64, fontSize: 24 }}>
                        {agentDetail.agent.user?.avatar
                            ? <img src={agentDetail.agent.user.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                            : (agentDetail.agent.user?.name?.[0] || '?').toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0 }}>{agentDetail.agent.user?.name || '—'}</h4>
                        <div className="crm-dim" style={{ fontSize: 12 }}>
                            {agentDetail.agent.user?.email || ''}
                            {agentDetail.agent.user?.phone ? ` · ${agentDetail.agent.user.phone}` : ''}
                        </div>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                            {agentDetail.agent.user?.language || ''}{agentDetail.agent.user?.region ? ` · ${agentDetail.agent.user.region}` : ''}
                        </div>
                    </div>
                </div>

                <div className="crm-kpis" style={{ marginBottom: 12 }}>
                    <div className="crm-kpi">
                        <div className="crm-kpi-val">{agentDetail.stats.totalLeads}</div>
                        <div className="crm-kpi-label">{ct.total}</div>
                    </div>
                    <div className="crm-kpi">
                        <div className="crm-kpi-val" style={{ color: '#059669' }}>{agentDetail.stats.convertedCount}</div>
                        <div className="crm-kpi-label">{ct.converted}</div>
                    </div>
                    <div className="crm-kpi">
                        <div className="crm-kpi-val" style={{ color: '#00B4D8' }}>{agentDetail.stats.conversionRate}%</div>
                        <div className="crm-kpi-label">{ct.conversionRate}</div>
                    </div>
                    <div className="crm-kpi">
                        <div className="crm-kpi-val">${(agentDetail.stats.convertedBudget || 0).toLocaleString()}</div>
                        <div className="crm-kpi-label">{ct.revenue}</div>
                    </div>
                </div>

                <div className="crm-pipeline-mini" style={{ marginBottom: 12 }}>
                    {Object.entries(agentDetail.stats.byStatus || {}).map(([status, count]) => (
                        <div key={status} className="crm-pipe-stage" style={{ borderLeftColor: STATUS_COLORS[status] || '#888' }}>
                            <span className="crm-pipe-count">{count}</span>
                            <span className="crm-pipe-label">{ct.statuses?.[status] || status}</span>
                        </div>
                    ))}
                </div>

                <label>{ct.role || 'Rol'}</label>
                <select
                    value={agentDetail.agent.role}
                    onChange={e => handleUpdateAgentRole(agentDetail.agent._id, e.target.value)}
                >
                    <option value="admin">Admin</option>
                    <option value="agent">Agente</option>
                    <option value="viewer">Viewer</option>
                </select>

                <div className="crm-form-actions" style={{ marginTop: 14 }}>
                    <button className="crm-btn-danger-sm" onClick={() => handleRemoveAgent(agentDetail.agent._id)}>
                        <Icons.Trash size={13} color="#e53935" /> {ct.removeAgent}
                    </button>
                    <button className="crm-btn-primary" onClick={() => setAgentDetail(null)}>
                        {ct.close}
                    </button>
                </div>
            </div>
        </div>
    );

    const formModal = showForm && (
        <div className="crm-form-overlay" onClick={() => setShowForm(false)}>
            <div className="crm-form-modal" onClick={e => e.stopPropagation()}>
                <h4>{editingLead ? ct.editLead : ct.newLead}</h4>
                <div className="crm-form-grid">
                    <div><label>{ct.firstName} *</label><input value={form.firstName || ''} onChange={e => setForm({ ...form, firstName: e.target.value })} /></div>
                    <div><label>{ct.lastName}</label><input value={form.lastName || ''} onChange={e => setForm({ ...form, lastName: e.target.value })} /></div>
                    <div><label>{ct.phone}</label><input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                    <div><label>{ct.email}</label><input type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                    <div><label>{ct.source}</label>
                        <select value={form.source || 'manual'} onChange={e => setForm({ ...form, source: e.target.value })}>
                            {Object.entries(ct.sources || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>
                    <div><label>{ct.status}</label>
                        <select value={form.status || 'nuevo'} onChange={e => setForm({ ...form, status: e.target.value })}>
                            {Object.entries(ct.statuses || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>
                    <div><label>{ct.budget}</label><input type="number" value={form.budget || ''} onChange={e => setForm({ ...form, budget: e.target.value })} /></div>
                    <div><label>{ct.product}</label><input value={form.product || ''} onChange={e => setForm({ ...form, product: e.target.value })} /></div>
                </div>
                <label>{ct.notes}</label>
                <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} />

                {/* ── Próxima acción ── alimenta la pestaña Agenda + crons.
                    Si el usuario rellena fecha+hora se guarda; si los borra,
                    se limpia la acción al guardar el lead. */}
                <div className="crm-action-section">
                    <div className="crm-action-head">
                        <span><Icons.Calendar size={14} /> {ct.agenda || 'Próxima acción'}</span>
                    </div>
                    <div className="crm-form-grid">
                        <div>
                            <label>Fecha</label>
                            <input type="date" value={form.actionDate || ''} onChange={e => setForm({ ...form, actionDate: e.target.value })} />
                        </div>
                        <div>
                            <label>Hora</label>
                            <input type="time" value={form.actionTime || ''} onChange={e => setForm({ ...form, actionTime: e.target.value })} />
                        </div>
                        <div>
                            <label>Tipo</label>
                            <select value={form.actionType || 'call'} onChange={e => setForm({ ...form, actionType: e.target.value })}>
                                <option value="call">📞 Llamar</option>
                                <option value="message">💬 Mensaje</option>
                                <option value="visit">📍 Visita</option>
                                <option value="email">✉️ Email</option>
                                <option value="other">⭐ Otra</option>
                            </select>
                        </div>
                        <div>
                            <label>Nota</label>
                            <input value={form.actionNote || ''} onChange={e => setForm({ ...form, actionNote: e.target.value })} placeholder="Pedir presupuesto, etc." />
                        </div>
                    </div>
                </div>

                {/* Historial de notas — solo en edición (necesita leadId).
                    Tras cada llamada/visita, el agente añade nota aquí. */}
                {editingLead?._id && (
                    <LeadNotes
                        leadId={editingLead._id}
                        currentUserId={currentUser?._id}
                    />
                )}

                <div className="crm-form-actions">
                    {editingLead && (
                        <button className="crm-btn-danger" onClick={() => { handleDeleteLead(editingLead._id); setShowForm(false); }}>
                            <Icons.Trash size={14} /> {ct.delete}
                        </button>
                    )}
                    <div style={{ flex: 1 }} />
                    <button className="crm-btn-secondary" onClick={() => setShowForm(false)}>{ct.cancel}</button>
                    <button className="crm-btn-primary" onClick={handleSaveLead}>
                        <Icons.Save size={14} color="white" /> {ct.save}
                    </button>
                </div>
            </div>
        </div>
    );

    if (embedded) {
        return (
            <div className="crm-embedded">
                {crmNav}
                {error && <div className="crm-error" onClick={() => setError('')}>{error}</div>}
                {crmBody}
                {formModal}
                {agentDetailModal}
            </div>
        );
    }

    const content = (
        <div className="crm-overlay" onClick={onClose}>
            <div className="crm-modal" onClick={e => e.stopPropagation()}>
                <div className="crm-header">
                    <button className="crm-icon-btn" onClick={onClose}><Icons.ArrowBack size={20} color="white" /></button>
                    <h3><Icons.Pulse size={18} /> {ct.title}</h3>
                    <button className="crm-icon-btn" onClick={onClose}><Icons.Close size={18} color="white" /></button>
                </div>
                {crmNav}
                {error && <div className="crm-error" onClick={() => setError('')}>{error}</div>}
                {crmBody}
                {formModal}
                {agentDetailModal}
            </div>
        </div>
    );

    return ReactDOM.createPortal(content, document.body);
}

export default CRMModal;
